/**
 * Permissions hook for Cipher - Phase D.
 *
 * Provides easy access to user permissions and feature gating.
 * Reads user subscription tier from auth store.
 */
import { useCallback, useMemo } from 'react'
import { useAuthStore } from '../stores/authStore'
import { SubscriptionTier, TIER_ORDER, TIER_NAMES } from '../api/billing'

export interface Permissions {
  // Limits
  max_watchlist: number
  max_portfolios: number
  max_positions_per_portfolio: number
  alert_count: number
  ai_queries_daily: number
  news_articles_daily: number
  // Boolean features
  backtester: boolean
  custom_strategies: boolean
  real_time_scores: boolean
  score_breakdown: boolean
  options_intelligence: boolean
  news_intelligence: boolean
  causal_network: boolean
  api_access: boolean
  priority_support: boolean
  advanced_alerts: boolean
  daily_brief: boolean
  // Other
  refresh_rate_seconds: number
}

// Permissions by tier
const TIER_PERMISSIONS: Record<SubscriptionTier, Permissions> = {
  free: {
    max_watchlist: 5,
    max_portfolios: 1,
    max_positions_per_portfolio: 10,
    alert_count: 3,
    ai_queries_daily: 0,
    news_articles_daily: 10,
    backtester: false,
    custom_strategies: false,
    real_time_scores: false,
    score_breakdown: false,
    options_intelligence: false,
    news_intelligence: false,
    causal_network: false,
    api_access: false,
    priority_support: false,
    advanced_alerts: false,
    daily_brief: true,
    refresh_rate_seconds: 300
  },
  informed: {
    max_watchlist: 25,
    max_portfolios: 3,
    max_positions_per_portfolio: 50,
    alert_count: 10,
    ai_queries_daily: 10,
    news_articles_daily: 50,
    backtester: false,
    custom_strategies: false,
    real_time_scores: false,
    score_breakdown: true,
    options_intelligence: false,
    news_intelligence: false,
    causal_network: false,
    api_access: false,
    priority_support: false,
    advanced_alerts: false,
    daily_brief: true,
    refresh_rate_seconds: 60
  },
  strategic: {  // "Pro" in marketing
    max_watchlist: -1,  // Unlimited
    max_portfolios: -1,
    max_positions_per_portfolio: -1,
    alert_count: -1,
    ai_queries_daily: 50,
    news_articles_daily: -1,
    backtester: true,
    custom_strategies: true,
    real_time_scores: true,
    score_breakdown: true,
    options_intelligence: true,
    news_intelligence: true,
    causal_network: true,
    api_access: false,
    priority_support: false,
    advanced_alerts: true,
    daily_brief: true,
    refresh_rate_seconds: 15
  },
  apex_pro: {  // "Elite" in marketing
    max_watchlist: -1,
    max_portfolios: -1,
    max_positions_per_portfolio: -1,
    alert_count: -1,
    ai_queries_daily: -1,  // Unlimited
    news_articles_daily: -1,
    backtester: true,
    custom_strategies: true,
    real_time_scores: true,
    score_breakdown: true,
    options_intelligence: true,
    news_intelligence: true,
    causal_network: true,
    api_access: true,
    priority_support: true,
    advanced_alerts: true,
    daily_brief: true,
    refresh_rate_seconds: 5
  }
}

// Feature to required tier mapping
const FEATURE_TIERS: Record<string, SubscriptionTier> = {
  // Pro tier features
  backtester: 'strategic',
  custom_strategies: 'strategic',
  real_time_scores: 'strategic',
  ai_analysis: 'strategic',
  options_intelligence: 'strategic',
  news_intelligence: 'strategic',
  causal_network: 'strategic',
  advanced_alerts: 'strategic',
  watchlist_unlimited: 'strategic',
  portfolio_multiple: 'strategic',
  // Elite tier features
  api_access: 'apex_pro',
  priority_support: 'apex_pro',
  unlimited_ai: 'apex_pro',
  // Lower tiers
  score_breakdown: 'informed'
}

// Upgrade prompt text
export interface UpgradePrompt {
  title: string
  price: string
  message: string
  features: string[]
  tier: SubscriptionTier
}

const UPGRADE_PROMPTS: Record<SubscriptionTier, UpgradePrompt> = {
  free: {
    title: 'Start Free',
    price: '$0/month',
    message: 'You are on the free tier.',
    features: [],
    tier: 'free'
  },
  informed: {
    title: 'Upgrade to Informed',
    price: '$9/month',
    message: 'Get more watchlist slots and score breakdown.',
    features: ['25 watchlist tickers', 'Score breakdown'],
    tier: 'informed'
  },
  strategic: {
    title: 'Upgrade to Cipher Pro',
    price: '$29/month',
    message: 'This feature requires Cipher Pro. Upgrade to unlock unlimited watchlist, AI analysis, backtesting, and more.',
    features: [
      'Unlimited watchlist',
      'AI analysis (50 queries/day)',
      'Full backtesting engine',
      'Real-time Cipher Scores',
      'Advanced alerts (14 types)',
      'Options intelligence',
      'Causal network visualization'
    ],
    tier: 'strategic'
  },
  apex_pro: {
    title: 'Upgrade to Cipher Elite',
    price: '$99/month',
    message: 'This feature requires Cipher Elite. Get unlimited AI, API access, and priority support.',
    features: [
      'Everything in Pro',
      'Unlimited AI analysis',
      'API access (build your own tools)',
      'Priority support (< 2hr response)',
      'Early access to new features'
    ],
    tier: 'apex_pro'
  }
}

export function usePermissions() {
  const user = useAuthStore((state) => state.user)

  // Get current tier
  const currentTier = useMemo((): SubscriptionTier => {
    return (user?.subscription_tier as SubscriptionTier) || 'free'
  }, [user?.subscription_tier])

  // Get permissions for current tier
  const permissions: Permissions = useMemo(() => {
    return TIER_PERMISSIONS[currentTier] || TIER_PERMISSIONS.free
  }, [currentTier])

  // Check if user can access a boolean feature
  const can = useCallback((feature: keyof Permissions): boolean => {
    const value = permissions[feature]
    return typeof value === 'boolean' ? value : true
  }, [permissions])

  // Check if a specific named feature is available
  const checkPermission = useCallback((feature: string): boolean => {
    const requiredTier = FEATURE_TIERS[feature]
    if (!requiredTier) return true
    const currentLevel = TIER_ORDER.indexOf(currentTier)
    const requiredLevel = TIER_ORDER.indexOf(requiredTier)
    return currentLevel >= requiredLevel
  }, [currentTier])

  // Get the required tier for a feature
  const requiresTier = useCallback((feature: string): SubscriptionTier => {
    return FEATURE_TIERS[feature] || 'informed'
  }, [])

  // Get a numeric limit value (-1 means unlimited)
  const getLimit = useCallback((limitName: keyof Permissions): number => {
    const value = permissions[limitName]
    return typeof value === 'number' ? value : 0
  }, [permissions])

  // Check if user is within a limit
  const withinLimit = useCallback((limitName: keyof Permissions, currentCount: number): boolean => {
    const limit = getLimit(limitName)
    if (limit === -1) return true  // Unlimited
    return currentCount < limit
  }, [getLimit])

  // Check if user has at least a certain tier
  const hasTier = useCallback((tier: SubscriptionTier): boolean => {
    const currentLevel = TIER_ORDER.indexOf(currentTier)
    const requiredLevel = TIER_ORDER.indexOf(tier)
    return currentLevel >= requiredLevel
  }, [currentTier])

  // Get the tier name
  const tierName = useMemo(() => TIER_NAMES[currentTier], [currentTier])

  // Get upgrade prompt for a feature
  const getUpgradePrompt = useCallback((feature: string): UpgradePrompt => {
    const requiredTier = FEATURE_TIERS[feature] || 'strategic'
    return UPGRADE_PROMPTS[requiredTier]
  }, [])

  // Boolean helpers for common checks
  const isProUser = useMemo(() => hasTier('strategic'), [hasTier])
  const isEliteUser = useMemo(() => hasTier('apex_pro'), [hasTier])
  const isFreeUser = useMemo(() => currentTier === 'free', [currentTier])
  const isPaidUser = useMemo(() => currentTier !== 'free', [currentTier])

  return {
    permissions,
    currentTier,
    tierName,
    can,
    checkPermission,
    requiresTier,
    getLimit,
    withinLimit,
    hasTier,
    getUpgradePrompt,
    // Convenience booleans
    isFreeUser,
    isPaidUser,
    isProUser,
    isEliteUser,
    // Legacy compatibility
    isPaid: isPaidUser,
    isStrategic: isProUser,
    isPro: isEliteUser
  }
}

export default usePermissions
